import React, { useState } from "react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label.jsx";
import axios from "axios";
import {
  StatusArr,
  getStatusDisplayLabel,
  StatusDotClasses,
} from "../../../utils/status-utils";

export default function StatusEditor({
  album,
  onStatusChange,
  disabled = false,
  className = "",
  triggerClassName = "w-48",
}) {
  const [visible, setVisible] = useState(false);
  const [reviewComments, setReviewComments] = useState(
    album.reviewComments || ""
  );
  const [pendingStatus, setPendingStatus] = useState(null);

  const handleStatusChange = (newStatus) => {
    if (newStatus === "Rejected") {
      setPendingStatus(newStatus);
      setVisible(true);
    } else {
      submitStatusChange(newStatus);
    }
  };

  const submitStatusChange = (status) => {
    const statusToSubmit = status || pendingStatus;

    axios
      .patch("/admin/albums/" + album._id + "/status", {
        status: statusToSubmit,
        reviewComments: statusToSubmit === "Rejected" ? reviewComments : "",
      })
      .then(() => {
        toast.success(
          `Album status for "${
            album.title
          }" has been set to ${getStatusDisplayLabel(statusToSubmit)}`
        );
        setVisible(false);
        setPendingStatus(null);

        // Call the parent callback with the updated album data
        if (onStatusChange) {
          const updatedAlbum = {
            ...album,
            status: statusToSubmit,
            reviewComments: statusToSubmit === "Rejected" ? reviewComments : "",
          };
          onStatusChange(updatedAlbum, statusToSubmit);
        }
      })
      .catch((error) => {
        toast.error(`Failed changing album status for "${album.title}"`);
        console.error("Status change error:", error);
      });
  };

  const handleCancel = () => {
    setVisible(false);
    setPendingStatus(null);
    setReviewComments(album.reviewComments || "");
  };

  return (
    <>
      <Select
        value={album.status}
        onValueChange={handleStatusChange}
        disabled={disabled}
      >
        <SelectTrigger className={triggerClassName}>
          <div className="flex items-center gap-2">
            <div
              className={`h-3 w-3 rounded-full ${
                StatusDotClasses[album.status] || "bg-gray-400"
              }`}
            />
            <SelectValue>{getStatusDisplayLabel(album.status)}</SelectValue>
          </div>
        </SelectTrigger>
        <SelectContent>
          {StatusArr.map((status) => (
            <SelectItem key={status} value={status}>
              <div className="flex items-center gap-2">
                <div
                  className={`h-3 w-3 rounded-full ${
                    StatusDotClasses[status] || "bg-gray-400"
                  }`}
                />
                {getStatusDisplayLabel(status)}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={visible} onOpenChange={setVisible}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rejection Comments</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="comments">
                Please provide a reason for rejecting this album:
              </Label>
              <Textarea
                id="comments"
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                placeholder="Enter rejection comments..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => submitStatusChange()}
              disabled={!reviewComments.trim()}
            >
              Reject Album
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
